import { useAppDispatch } from "hooks/redux";
import React, { useCallback, useMemo } from "react";
import { websiteBuilderUpdateSelectedNode } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { TWebsiteBuilderSectionEntity } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderSectionEntity";
import { isWebsiteBuilderGrid } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderGrid";
import { isWebsiteBuilderElement } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderElement";
import { isWebsiteBuilderButton } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderButton";
import { isWebsiteBuilderDivider } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderDivider";
import { isWebsiteBuilderEmbed } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderEmbed";
import { isWebsiteBuilderImage } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderImage";
import { isWebsiteBuilderIcon } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderIcon";
import { isWebsiteBuilderLink } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderLink";
import { isWebsiteBuilderList } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderList";
import { isWebsiteBuilderLogo } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderLogo";
import { isWebsiteBuilderNavigationMenu } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderNavigationMenu";
import { isWebsiteBuilderVideo } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderVideo";
import { isWebsiteBuilderFlex } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderFlex";
import { isWebsiteBuilderContainer } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderContainer";
import { isWebsiteBuilderSlideshow } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderSlideshow";
import { isWebsiteBuilderBox } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderBox";
import { isWebsiteBuilderTabset } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderTabset";
import { isWebsiteBuilderMarquee } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderMarquee";
import { ButtonSettings } from "./settings/element/button/ButtonSettings";
import { LogoSettings } from "./settings/element/logo/LogoSettings";
import { EmbedSettings } from "./settings/element/embed/EmbedSettings";
import { DividerSettings } from "./settings/element/divider/DividerSettings";
import { ListSettings } from "./settings/element/list/ListSettings";
import { LinkSettings } from "./settings/element/link/LinkSettings";
import { ImageSettings } from "./settings/element/image/ImageSettings";
import { VideoSettings } from "./settings/element/video/VideoSettings";
import { BoxSettings } from "./settings/container/box/BoxSettings";
import { FlexSettings } from "./settings/container/flex/FlexSettings";
import { GridSettings } from "./settings/container/grid/GridSettings";
import { MarqueeSettings } from "./settings/container/marquee/MarqueeSettings";
import { SlideshowSettings } from "./settings/container/slideshow/SlideshowSettings";
import { TabsetSettings } from "./settings/container/tabset/TabsetSettings";
import Portal from "elements/Portal";
import { WEBSITE_BUILDER_PANEL_ID } from "pages/secure/facility/websiteBuilder/editor/shell/panel/WebsiteBuilderPanel";
import { IconSettings } from "./settings/element/icon/IconSettings";
import { useWebsiteBuilderEditorContext } from "pages/secure/facility/websiteBuilder/editor/websiteBuilderEditorContext";

export interface IUseSectionEntityViewSettingsArgs {
  node: TWebsiteBuilderSectionEntity;
  selected: boolean;
  settingsActive: boolean;
}

interface IUseSectionEntityViewSettings {
  viewSettings: JSX.Element;
  handleViewSettingsDoubleClick: () => void;
}

export function useSectionEntityViewSettings(args: IUseSectionEntityViewSettingsArgs): IUseSectionEntityViewSettings {
  const dispatch = useAppDispatch();

  const { handleSetToolbarMenuType } = useWebsiteBuilderEditorContext();

  const closeSettings = useCallback(() => {
    if (args.node == null) {
      return;
    }

    dispatch(
      websiteBuilderUpdateSelectedNode({
        selectedNodeUpdates: {
          type: args.node.type === "element" ? "element" : "container",
          editingMode: {
            type: "none",
          },
        },
      }),
    );
  }, [args.node]);

  const renderViewSettingsContent: () => JSX.Element = useMemo(() => {
    const node = args.node;

    if (isWebsiteBuilderElement(node)) {
      if (isWebsiteBuilderButton(node)) {
        return function renderButtonSettings() {
          return (
            <ButtonSettings key={node.id} id={node.id} settings={node.element.settings} closeSettings={closeSettings} />
          );
        };
      } else if (isWebsiteBuilderDivider(node)) {
        return function renderDividerSettings() {
          return (
            <DividerSettings key={node.id} id={node.id} settings={node.element.settings} closeSettings={closeSettings} />
          );
        };
      } else if (isWebsiteBuilderEmbed(node)) {
        return function renderEmbedSettings() {
          return (
            <EmbedSettings key={node.id} id={node.id} settings={node.element.settings} closeSettings={closeSettings} />
          );
        };
      } else if (isWebsiteBuilderIcon(node)) {
        return function renderIconSettings() {
          return <IconSettings key={node.id} id={node.id} settings={node.element.settings} closeSettings={closeSettings} />;
        };
      } else if (isWebsiteBuilderImage(node)) {
        return function renderImageSettings() {
          return (
            <ImageSettings key={node.id} id={node.id} settings={node.element.settings} closeSettings={closeSettings} />
          );
        };
      } else if (isWebsiteBuilderLink(node)) {
        return function renderLinkSettings() {
          return (
            <LinkSettings key={node.id} id={node.id} settings={node.element.settings} closeSettings={closeSettings} />
          );
        };
      } else if (isWebsiteBuilderList(node)) {
        return function renderListSettings() {
          return (
            <ListSettings key={node.id} id={node.id} settings={node.element.settings} closeSettings={closeSettings} />
          );
        };
      } else if (isWebsiteBuilderLogo(node)) {
        return function renderLogoSettings() {
          return (
            <LogoSettings key={node.id} id={node.id} settings={node.element.settings} closeSettings={closeSettings} />
          );
        };
      } else if (isWebsiteBuilderVideo(node)) {
        return function renderVideoSettings() {
          return (
            <VideoSettings key={node.id} id={node.id} settings={node.element.settings} closeSettings={closeSettings} />
          );
        };
      } else {
        return null;
      }
    } else if (isWebsiteBuilderContainer(node)) {
      if (isWebsiteBuilderBox(node)) {
        return function renderBoxSettings() {
          return (
            <BoxSettings key={node.id} id={node.id} settings={node.container.settings} closeSettings={closeSettings} />
          );
        };
      } else if (isWebsiteBuilderFlex(node)) {
        return function renderFlexSettings() {
          return (
            <FlexSettings key={node.id} id={node.id} settings={node.container.settings} closeSettings={closeSettings} />
          );
        };
      } else if (isWebsiteBuilderGrid(node)) {
        return function renderGridSettings() {
          return (
            <GridSettings key={node.id} id={node.id} settings={node.container.settings} cells={node.children} closeSettings={closeSettings} />
          );
        };
      } else if (isWebsiteBuilderMarquee(node)) {
        return function renderMarqueeSettings() {
          return (
            <MarqueeSettings key={node.id} id={node.id} settings={node.container.settings} closeSettings={closeSettings} />
          );
        };
      } else if (isWebsiteBuilderSlideshow(node)) {
        return function renderSlideshowSettings() {
          return (
            <SlideshowSettings key={node.id} id={node.id} settings={node.container.settings} closeSettings={closeSettings} />
          );
        };
      } else if (isWebsiteBuilderTabset(node)) {
        return function renderTabsetSettings() {
          return (
            <TabsetSettings key={node.id} node={node} closeSettings={closeSettings} />
          );
        };
      } else {
        return null;
      }
    } else {
      return null;
    }
  }, [args.node, closeSettings]);

  function handleViewSettingsDoubleClick() {
    if (args.selected && isWebsiteBuilderElement(args.node) && isWebsiteBuilderNavigationMenu(args.node)) {
      handleSetToolbarMenuType?.("manage-pages");

      return;
    }

    if (args.node == null || !args.selected || renderViewSettingsContent == null) {
      return;
    }

    if (args.node.type === "element") {
      dispatch(
        websiteBuilderUpdateSelectedNode({
          selectedNodeUpdates: {
            type: "element",
            editingMode: {
              type: args.settingsActive ? "none" : "element-settings",
            },
          },
        }),
      );
    } else if (args.node.type === "container") {
      dispatch(
        websiteBuilderUpdateSelectedNode({
          selectedNodeUpdates: {
            type: "container",
            editingMode: {
              type: args.settingsActive ? "none" : "container-settings",
            },
          },
        }),
      );
    }
  }

  function renderViewSettings() {
    if (!args.settingsActive || renderViewSettingsContent == null) {
      return null;
    }

    return (
      <Portal parentId={WEBSITE_BUILDER_PANEL_ID}>
        {renderViewSettingsContent()}
      </Portal>
    );
  }

  return {
    viewSettings: renderViewSettings(),
    handleViewSettingsDoubleClick,
  };
}